// Supplier Repair Types and Interfaces

/**
 * สถานะการส่งซ่อม
 */
export enum SupplierRepairStatus {
  DRAFT = 'draft',           // ร่าง - ยังไม่ส่ง
  SENT = 'sent',             // ส่งไปซ่อมแล้ว
  IN_REPAIR = 'in_repair',   // กำลังซ่อม
  READY = 'ready',           // ซ่อมเสร็จแล้ว - รอรับ
  RETURNED = 'returned',     // รับอุปกรณ์คืนแล้ว
  COMPLETED = 'completed',   // เสร็จสมบูรณ์
  CANCELLED = 'cancelled'    // ยกเลิก
}

/**
 * ระดับความสำคัญ
 */
export enum RepairPriority {
  LOW = 'low',       // ไม่เร่งด่วน
  NORMAL = 'normal', // ปกติ
  HIGH = 'high',     // เร่งด่วน
  URGENT = 'urgent'  // ด่วนมาก
}

/**
 * ข้อมูลผู้ใช้แบบพื้นฐาน
 */
export interface UserBasic {
  id: number;
  username: string;
  full_name?: string | null;
}

/**
 * ข้อมูลอุปกรณ์แบบพื้นฐาน
 */
export interface DeviceBasic {
  id: number;
  code: string;
  serial_number?: string | null;
  model_name?: string | null;
}

/**
 * ข้อมูลซัพพลายเออร์แบบพื้นฐาน
 */
export interface SupplierBasic {
  id: number;
  code: string;
  name: string;
  phone?: string | null;
}

/**
 * รายการอุปกรณ์ภายในใบส่งซ่อม (1 ใบ → หลายเครื่อง)
 * status/ค่าซ่อม/วันที่ เป็นระดับใบ — item เก็บแค่ความสัมพันธ์อุปกรณ์
 */
/**
 * ผลซ่อม **รายเครื่อง** — ตัวตัดสินว่าเครื่องนั้นกลับเข้าคลังหรือไม่
 *
 * แยกจาก SupplierRepairStatus โดยตั้งใจ: status = ใบอยู่ตรงไหนในคิวของร้าน (ระดับเอกสาร)
 * ส่วน result = เครื่องนี้ได้ผลอะไร (ระดับของจริง)
 */
export enum SupplierRepairResult {
  REPAIRED = 'repaired',            // ซ่อมได้ → กลับเข้าคลัง/สัญญาตามเดิม
  UNREPAIRABLE = 'unrepairable',    // ซ่อมไม่ได้ → ชำรุด ไม่กลับเข้า pool
  LOST = 'lost'                     // ร้านทำหาย/ไม่ได้คืน → สูญหาย ไม่กลับเข้า pool
}

export const getRepairResultLabel = (result?: string | null): string => {
  switch (result) {
    case SupplierRepairResult.REPAIRED: return 'ซ่อมได้';
    case SupplierRepairResult.UNREPAIRABLE: return 'ซ่อมไม่ได้';
    case SupplierRepairResult.LOST: return 'ร้านทำหาย/ไม่ได้คืน';
    default: return '-';
  }
};

export interface SupplierRepairItem {
  id: number;
  device_id: number;
  device?: DeviceBasic | null;
  /** ผลซ่อมของเครื่องนี้ · null = ยังไม่บันทึกผล (ใบที่ยังไม่ถึงรับคืน หรือใบเก่าก่อน 2026-09-09) */
  repair_result?: SupplierRepairResult | null;
  result_note?: string | null;
  /** ค่าซ่อมเฉพาะเครื่องนี้ (ยอดรวมของใบยังอยู่ที่ actual_cost) */
  item_cost?: number | null;
  returned_date?: string | null;
}

/** ผลซ่อมของเครื่องเดียวที่ส่งไปพร้อมการเปลี่ยนสถานะเป็น RETURNED */
export interface SupplierRepairItemResultInput {
  item_id?: number;
  device_id?: number;
  result: SupplierRepairResult;
  note?: string;
  cost?: number;
}

/**
 * ประวัติการเปลี่ยนสถานะ
 */
export interface SupplierRepairStatusHistory {
  id: number;
  old_status?: string | null;
  new_status: string;
  change_reason?: string | null;
  changed_at: string;
  changed_by?: UserBasic | null;
}

/**
 * ข้อมูลการส่งซ่อม
 */
export interface SupplierRepair {
  id: number;
  request_number: string;                // รหัสการส่งซ่อม (เช่น REP-20260106-0001)
  device_id: number;                     // รหัสอุปกรณ์
  supplier_id: number;                   // รหัสซัพพลายเออร์
  maintenance_id?: number | null;        // รหัสการซ่อมบำรุงที่เกี่ยวข้อง

  // ข้อมูลการซ่อม
  reported_symptoms: string;             // อาการที่พบ/รายละเอียดปัญหา
  diagnosis?: string | null;             // การวินิจฉัย
  repair_description?: string | null;    // รายละเอียดการซ่อม
  priority: RepairPriority;              // ระดับความสำคัญ
  quoted_cost?: number | null;           // ค่าใช้จ่ายโดยประมาณ
  actual_cost?: number | null;           // ค่าใช้จ่ายจริง
  is_warranty_claim?: boolean;           // เคลมประกันหรือไม่

  // วันที่
  sent_date?: string | null;             // วันที่ส่งซ่อม
  expected_return_date?: string | null;  // วันที่คาดว่าจะได้รับคืน
  actual_return_date?: string | null;    // วันที่ได้รับคืนจริง

  // สถานะและหมายเหตุ
  status: SupplierRepairStatus;          // สถานะ
  notes?: string | null;                 // หมายเหตุ
  condition_before?: string | null;      // สภาพก่อนซ่อม
  condition_after?: string | null;       // สภาพหลังซ่อม

  // ข้อมูลผู้ทำรายการ
  created_by_id?: number | null;
  updated_by_id?: number | null;

  // Relationships
  device?: DeviceBasic | null;          // เครื่องตัวแทน/เครื่องแรก (backward compat)
  items?: SupplierRepairItem[];         // อุปกรณ์ทุกเครื่องในใบ (batch)
  supplier?: SupplierBasic | null;
  // backend MaintenanceBasic = { id, details, status }
  maintenance?: { id: number; maintenance_number?: string; details?: string | null; status?: string } | null;
  created_by?: UserBasic | null;
  updated_by?: UserBasic | null;
  status_history?: SupplierRepairStatusHistory[];

  // Timestamps
  created_at: string;
  updated_at?: string | null;

  // Computed properties
  days_in_repair?: number | null;        // จำนวนวันที่ส่งซ่อม
  is_overdue?: boolean | null;           // เกินกำหนดหรือไม่
}

/**
 * ฟอร์มสำหรับสร้างการส่งซ่อมใหม่
 */
export interface SupplierRepairCreate {
  device_ids: number[];          // เลือกได้หลายเครื่อง (อย่างน้อย 1) — ส่งแทน device_id เดี่ยว
  device_id?: number;            // backward compat (ถ้าส่งเครื่องเดียว) — ของใหม่ใช้ device_ids
  supplier_id: number;
  maintenance_id?: number;
  reported_symptoms: string;
  diagnosis?: string;
  repair_description?: string;
  priority?: RepairPriority;
  quoted_cost?: number;
  is_warranty_claim?: boolean;
  sent_date?: string;
  expected_return_date?: string;
  notes?: string;
  condition_before?: string;
}

/**
 * ฟอร์มสำหรับแก้ไขการส่งซ่อม
 */
export interface SupplierRepairUpdate {
  device_id?: number;
  supplier_id?: number;
  maintenance_id?: number;
  reported_symptoms?: string;
  diagnosis?: string | null; // null = ล้างค่า (ผู้ใช้ลบจนว่าง)
  repair_description?: string;
  priority?: RepairPriority;
  quoted_cost?: number | null;
  actual_cost?: number;
  is_warranty_claim?: boolean;
  sent_date?: string;
  expected_return_date?: string;
  actual_return_date?: string;
  notes?: string | null;
  condition_before?: string | null;
  condition_after?: string;
}

/**
 * ข้อมูลสำหรับการเปลี่ยนสถานะ
 */
export interface SupplierRepairStatusChange {
  new_status: SupplierRepairStatus;
  /** สถานะที่จอแสดงตอนกดปุ่ม — ส่งไปให้ backend ตรวจ (optimistic locking) · ไม่ตรง = 409 */
  expected_current_status?: SupplierRepairStatus;
  change_reason?: string;
  actual_return_date?: string;
  condition_after?: string;
  actual_cost?: number;
  /**
   * ผลซ่อมรายเครื่อง (เฉพาะตอนเปลี่ยนเป็น RETURNED)
   * ใบที่มีหลายเครื่อง **ต้องส่งครบทุกเครื่อง** ไม่งั้น backend ตอบ 400 ระบุเครื่องที่ขาด
   */
  item_results?: SupplierRepairItemResultInput[];
}

/**
 * Response สำหรับรายการการส่งซ่อม
 */
export interface SupplierRepairListResponse {
  items: SupplierRepair[];
  total: number;
  page: number;
  limit: number;
}

/**
 * สถิติภาพรวมของการส่งซ่อม
 */
export interface SupplierRepairDashboardStats {
  total: number;                   // จำนวนทั้งหมด
  draft: number;                   // สถานะ: รอส่ง
  sent: number;                    // สถานะ: ส่งแล้ว
  in_repair: number;               // สถานะ: กำลังซ่อม
  ready: number;                   // สถานะ: พร้อมรับคืน
  returned: number;                // สถานะ: รับคืนแล้ว
  completed: number;               // สถานะ: เสร็จสมบูรณ์
  cancelled: number;               // สถานะ: ยกเลิก
  overdue: number;                 // เกินกำหนด
  urgent_count: number;            // ด่วนมาก
  high_priority_count: number;     // เร่งด่วน
  total_quoted_cost: string;       // ค่าใช้จ่ายโดยประมาณรวม
  total_actual_cost: string;       // ค่าใช้จ่ายจริงรวม
  warranty_claims: number;         // จำนวนเคลมประกัน
  this_month: number;              // สร้างเดือนนี้
  completed_this_month: number;    // ปิดงานจริงเดือนนี้ (BUG-S175-REPAIRKPI)
  last_month: number;              // เดือนที่แล้ว
}

/**
 * พารามิเตอร์สำหรับดึงรายการส่งซ่อม
 */
export interface GetSupplierRepairsParams {
  page?: number;
  limit?: number;
  search?: string;
  status?: SupplierRepairStatus;
  priority?: RepairPriority;
  supplier_id?: number;
  device_id?: number;
  is_overdue?: boolean;
  from_date?: string;
  to_date?: string;
}

// ==========================================================================
// Helper Functions
// ==========================================================================

/**
 * แปลงสถานะเป็นข้อความภาษาไทย
 */
export const getStatusLabel = (status: SupplierRepairStatus): string => {
  const labels: Record<SupplierRepairStatus, string> = {
    [SupplierRepairStatus.DRAFT]: 'ร่าง',
    [SupplierRepairStatus.SENT]: 'ส่งซ่อมแล้ว',
    [SupplierRepairStatus.IN_REPAIR]: 'กำลังซ่อม',
    [SupplierRepairStatus.READY]: 'ซ่อมเสร็จเรียบร้อย - รอรับคืน',
    [SupplierRepairStatus.RETURNED]: 'รับคืนแล้ว',
    [SupplierRepairStatus.COMPLETED]: 'เสร็จสมบูรณ์',
    [SupplierRepairStatus.CANCELLED]: 'ยกเลิก'
  };
  return labels[status] || status;
};

/**
 * แปลงสถานะเป็นสี
 */
export const getStatusColor = (status: SupplierRepairStatus): string => {
  const colors: Record<SupplierRepairStatus, string> = {
    [SupplierRepairStatus.DRAFT]: 'gray',
    [SupplierRepairStatus.SENT]: 'blue',
    [SupplierRepairStatus.IN_REPAIR]: 'orange',
    [SupplierRepairStatus.READY]: 'green',
    [SupplierRepairStatus.RETURNED]: 'teal',
    [SupplierRepairStatus.COMPLETED]: 'emerald',
    [SupplierRepairStatus.CANCELLED]: 'red'
  };
  return colors[status] || 'gray';
};

/**
 * แปลงระดับความสำคัญเป็นข้อความภาษาไทย
 */
export const getPriorityLabel = (priority: RepairPriority): string => {
  const labels: Record<RepairPriority, string> = {
    [RepairPriority.LOW]: 'ไม่เร่งด่วน',
    [RepairPriority.NORMAL]: 'ปกติ',
    [RepairPriority.HIGH]: 'เร่งด่วน',
    [RepairPriority.URGENT]: 'ด่วนมาก'
  };
  return labels[priority] || priority;
};

/**
 * แปลงระดับความสำคัญเป็นสี
 */
export const getPriorityColor = (priority: RepairPriority): string => {
  const colors: Record<RepairPriority, string> = {
    [RepairPriority.LOW]: 'gray',
    [RepairPriority.NORMAL]: 'blue',
    [RepairPriority.HIGH]: 'orange',
    [RepairPriority.URGENT]: 'red'
  };
  return colors[priority] || 'gray';
};
